import { create } from 'zustand';
import api from '@/lib/api';
import type { BrandingConfig, ApiResponse } from '@/types';

const DEFAULT_BRANDING: BrandingConfig = {
  // Neutral fallback only — the real name comes from GET /clinic/branding at
  // runtime. Nothing clinic-specific is baked into the build anymore.
  name: 'Dental CRM',
  tagline: '',
  logo_url: '',
  primary_color: 'oklch(0.55 0.19 250)',
  email_from: '',
  email_name: '',
  // The Phase 60 base design — what :root/.dark already render.
  app_design: 'apollo',
};

interface BrandingState {
  branding: BrandingConfig | null;
  isLoaded: boolean;
  fetchBranding: () => Promise<void>;
}

export const useBrandingStore = create<BrandingState>()((set) => ({
  branding: null,
  isLoaded: false,

  fetchBranding: async () => {
    try {
      const response = await api.get<ApiResponse<BrandingConfig>>('/clinic/branding');
      set({ branding: response.data.data, isLoaded: true });
    } catch {
      set({ branding: DEFAULT_BRANDING, isLoaded: true });
    }
  },
}));

export function useBranding(): BrandingConfig {
  const branding = useBrandingStore((state) => state.branding);
  return branding ?? DEFAULT_BRANDING;
}

/**
 * The clinic's app design (260910) — 'apollo' is the Phase 60 base every clinic
 * renders unless its profile says otherwise.
 *
 * Most of a design is tokens and needs no JS at all (globals.css keys off the
 * `data-design` attribute). Use this ONLY where a design changes something CSS
 * cannot express, so the conditional stays visible and countable.
 */
export function useAppDesign(): string {
  return useBranding().app_design ?? "apollo";
}
